import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation } from 'react-router-dom';

const TERMS_ACCEPTANCE_KEY = 'eq_terms_accepted';
const TERMS_VERSION = '1.0';
const MODAL_MINIMIZED_KEY = 'eq_terms_modal_minimized';

export default function TermsAcceptanceModal() {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const [showModal, setShowModal] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  useEffect(() => {
    if (isAuthenticated && user) {
      // Check if user has accepted current version of terms
      const acceptance = localStorage.getItem(TERMS_ACCEPTANCE_KEY);
      
      if (acceptance) {
        try {
          const { version, userId } = JSON.parse(acceptance);
          // Show modal if version changed or different user
          if (version !== TERMS_VERSION || userId !== user.sub) {
            setShowModal(true);
          }
        } catch (e) {
          setShowModal(true);
        }
      } else {
        // No acceptance record - show modal
        setShowModal(true);
      }
    }
  }, [isAuthenticated, user]);

  // Hide modal when on legal pages
  useEffect(() => {
    const isLegalPage = location.pathname.startsWith('/legal/');
    if (isLegalPage) {
      setIsMinimized(true);
      sessionStorage.setItem(MODAL_MINIMIZED_KEY, 'true');
    } else {
      // Restore modal when leaving legal pages
      const wasMinimized = sessionStorage.getItem(MODAL_MINIMIZED_KEY);
      if (wasMinimized === 'true') {
        setIsMinimized(false);
        sessionStorage.removeItem(MODAL_MINIMIZED_KEY);
      }
    }
  }, [location]);

  const handleAccept = () => {
    if (!accepted) return;

    const acceptanceRecord = {
      version: TERMS_VERSION,
      userId: user.sub,
      timestamp: new Date().toISOString(),
      email: user.email,
    };

    localStorage.setItem(TERMS_ACCEPTANCE_KEY, JSON.stringify(acceptanceRecord));
    setShowModal(false);
  };

  if (!showModal || isMinimized) return null;

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto bg-black/60 backdrop-blur-sm">
      <div className="flex min-h-full items-center justify-center p-2 sm:p-4">
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-4 sm:px-8 py-4 sm:py-6 text-white">
            <div className="flex items-center gap-3 mb-2">
              <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h2 className="text-xl sm:text-2xl font-bold">Welcome to EQ Assistant!</h2>
            </div>
            <p className="text-indigo-100 text-xs sm:text-sm">
              Before you get started, please review and accept our terms
            </p>
          </div>

          {/* Content */}
          <div className="px-4 sm:px-8 py-4 sm:py-6 overflow-y-auto max-h-[45vh] sm:max-h-[50vh]">
            <div className="prose prose-sm max-w-none">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Terms of Service & Privacy Policy</h3>
              
              <p className="text-gray-700 mb-4">
                To use EQ Assistant, you must agree to our Terms of Service and Privacy Policy. 
                These documents explain:
              </p>

              <ul className="space-y-2 text-gray-700 mb-4">
                <li className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>How we collect, use, and protect your personal information</span>
                </li>
                <li className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Your rights under GDPR and CCPA (access, deletion, portability)</span>
                </li>
                <li className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Acceptable use of the platform and prohibited activities</span>
                </li>
                <li className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Subscription terms, refund policy, and credit usage</span>
                </li>
                <li className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Limitation of liability and dispute resolution</span>
                </li>
              </ul>

              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-700">
                      <strong>Your Privacy Matters:</strong> We're GDPR and CCPA compliant. 
                      Your data is encrypted and secure. You can request deletion or export of 
                      your data at any time.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <a 
                  href="/legal/terms"
                  className="flex-1 text-center px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm border border-gray-300"
                >
                  📄 Read Terms of Service
                </a>
                <a 
                  href="/legal/privacy"
                  className="flex-1 text-center px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm border border-gray-300"
                >
                  🔒 Read Privacy Policy
                </a>
              </div>
            </div>
          </div>

          {/* Acceptance Checkbox */}
          <div className="px-4 sm:px-8 py-3 sm:py-4 bg-gray-50 border-t border-gray-200">
            <label className="flex items-start gap-2 sm:gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                className="mt-0.5 sm:mt-1 w-4 h-4 sm:w-5 sm:h-5 text-indigo-600 border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 cursor-pointer flex-shrink-0"
              />
              <span className="flex-1 text-xs sm:text-sm text-gray-700 group-hover:text-gray-900">
                I have read and agree to the{' '}
                <a 
                  href="/legal/terms"
                  className="text-indigo-600 hover:text-indigo-700 font-semibold underline"
                >
                  Terms of Service
                </a>
                {' '}and{' '}
                <a 
                  href="/legal/privacy"
                  className="text-indigo-600 hover:text-indigo-700 font-semibold underline"
                >
                  Privacy Policy
                </a>
              </span>
            </label>
          </div>

          {/* Footer */}
          <div className="px-4 sm:px-8 py-4 sm:py-6 bg-white border-t border-gray-200">
            <button
              onClick={handleAccept}
              disabled={!accepted}
              className={`w-full px-4 sm:px-6 py-3 sm:py-4 rounded-lg font-semibold text-white text-sm sm:text-base transition-all shadow-lg ${
                accepted
                  ? 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 cursor-pointer'
                  : 'bg-gray-300 cursor-not-allowed opacity-60'
              }`}
            >
              {accepted ? 'Continue to EQ Assistant →' : 'Please accept to continue'}
            </button>
            
            <p className="mt-3 sm:mt-4 text-center text-[10px] sm:text-xs text-gray-500 px-2">
              By clicking continue, you confirm that you are at least 18 years old and agree to be bound by our terms
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

